import { AJUDA_PRODUCAO } from "@/lib/ajuda/dialogos/producao";
import { DndContext, PointerSensor, closestCenter, useSensor, useSensors, type DragEndEvent } from "@dnd-kit/core";
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Eye, EyeOff, GripVertical, Lock, RotateCcw } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@bookdental/ui";
import { cn } from "@/lib/cn";
import {
  COLUMN_LABELS,
  COLUNAS_FIXAS,
  ehPadrao,
  fixarExtremos,
  type ColumnKey,
} from "./production-columns";

type Props = {
  aberto: boolean;
  ordem: ColumnKey[];
  ocultas: Set<ColumnKey>;
  onOrdem: (ordem: ColumnKey[]) => void;
  onOcultas: (ocultas: Set<ColumnKey>) => void;
  onRestaurar: () => void;
  onFechar: () => void;
};

/**
 * Ordem e visibilidade das colunas da planilha.
 *
 * Reordenar acontece principalmente ARRASTANDO O CABEÇALHO, direto na tabela — é o gesto que a
 * pessoa espera e não custa abrir nada. Este painel existe pro que o arraste não faz: **esconder**
 * coluna (o problema maior — são 18, e a maioria dos laboratórios opera olhando seis), voltar ao
 * padrão, e reordenar por teclado, já que arrastar cabeçalho é gesto de ponteiro.
 *
 * Os dois caminhos escrevem a mesma preferência: mexer aqui reflete na tabela e vice-versa.
 */
export function ColunasDialog({ aberto, ordem, ocultas, onOrdem, onOcultas, onRestaurar, onFechar }: Props) {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));
  const moveis = ordem.filter((k) => !COLUNAS_FIXAS.includes(k));

  const aoSoltar = (e: DragEndEvent) => {
    if (!e.over || e.active.id === e.over.id) return;
    const de = moveis.indexOf(e.active.id as ColumnKey);
    const para = moveis.indexOf(e.over.id as ColumnKey);
    if (de === -1 || para === -1) return;
    onOrdem(fixarExtremos(arrayMove(moveis, de, para)));
  };

  const alternar = (k: ColumnKey) => {
    const proximas = new Set(ocultas);
    if (proximas.has(k)) proximas.delete(k);
    else proximas.add(k);
    onOcultas(proximas);
  };

  const visiveis = moveis.length - [...ocultas].length;

  return (
    <Dialog open={aberto} onOpenChange={(v) => !v && onFechar()}>
      <DialogContent
        closeLabel="Fechar configuração de colunas"
        className="flex max-h-[85dvh] max-w-md flex-col gap-4 overflow-hidden border-zinc-200 bg-white shadow-xl dark:border-border dark:bg-card"
      >
        <DialogHeader>
          <DialogTitle className="dark:text-foreground" ajuda={AJUDA_PRODUCAO.colunas}>Colunas da planilha</DialogTitle>
          <DialogDescription className="dark:text-muted-foreground">
            Desmarque o que não usa. A ordem também pode ser mudada arrastando o cabeçalho direto na
            tabela. Vale só neste navegador.
          </DialogDescription>
        </DialogHeader>

        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={aoSoltar}>
            <SortableContext items={moveis} strategy={verticalListSortingStrategy}>
              <ul className="space-y-1">
                {moveis.map((k) => (
                  <LinhaDeColuna
                    key={k}
                    chave={k}
                    oculta={ocultas.has(k)}
                    onAlternar={() => alternar(k)}
                  />
                ))}
              </ul>
            </SortableContext>
          </DndContext>

          <p className="mt-3 flex items-center gap-1.5 text-[11px] text-zinc-400 dark:text-muted-foreground/80">
            <Lock className="h-3 w-3 shrink-0" />
            Seleção e Finalizar ficam fixas nas pontas — são ações, não dados.
          </p>
        </div>

        <div className="flex items-center justify-between gap-2 border-t border-zinc-100 pt-3 dark:border-border">
          <button
            type="button"
            disabled={ehPadrao(ordem, ocultas)}
            onClick={onRestaurar}
            className="inline-flex h-9 items-center gap-1.5 rounded-md px-2.5 text-[13px] font-medium text-zinc-600 transition-colors hover:bg-zinc-100 disabled:opacity-40 dark:text-muted-foreground dark:hover:bg-muted"
          >
            <RotateCcw className="h-3.5 w-3.5" />
            Restaurar padrão
          </button>
          <span className="text-[11px] text-zinc-500 dark:text-muted-foreground">
            {visiveis} de {moveis.length} colunas à vista
          </span>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function LinhaDeColuna({
  chave,
  oculta,
  onAlternar,
}: {
  chave: ColumnKey;
  oculta: boolean;
  onAlternar: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: chave });

  return (
    <li
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cn(
        "flex items-center gap-2 rounded-md border border-zinc-200 bg-white px-2 py-1.5 dark:border-border dark:bg-card",
        isDragging && "z-10 shadow-md",
        oculta && "opacity-60",
      )}
    >
      <button
        type="button"
        {...attributes}
        {...listeners}
        className="flex h-8 w-8 shrink-0 cursor-grab touch-none items-center justify-center rounded text-zinc-400 hover:text-zinc-600 active:cursor-grabbing dark:text-muted-foreground/60 dark:hover:text-foreground"
        aria-label={`Arrastar ${COLUMN_LABELS[chave]}`}
      >
        <GripVertical className="h-4 w-4" />
      </button>

      <span
        className={cn(
          "min-w-0 flex-1 truncate text-[13px]",
          oculta
            ? "text-zinc-400 line-through dark:text-muted-foreground"
            : "text-zinc-800 dark:text-foreground",
        )}
      >
        {COLUMN_LABELS[chave]}
      </span>

      <button
        type="button"
        onClick={onAlternar}
        aria-pressed={!oculta}
        aria-label={oculta ? `Mostrar ${COLUMN_LABELS[chave]}` : `Esconder ${COLUMN_LABELS[chave]}`}
        className="flex h-8 w-8 shrink-0 items-center justify-center rounded text-zinc-500 transition-colors hover:bg-zinc-100 dark:text-muted-foreground dark:hover:bg-muted"
      >
        {oculta ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
      </button>
    </li>
  );
}
